import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  BookOpen, CalendarDays, Users, ShoppingCart, Award, ClipboardCheck, FileDown,
  HardDrive, GraduationCap, FolderCog, CalendarCheck, UserCog, FileSignature,
  ShieldCheck,
} from "lucide-react";
import { adminApi } from "../api";

/**
 * Sub-menu unificado para todas as páginas relacionadas com um curso.
 *
 * Props:
 * - cursoId: ID do curso seleccionado (string)
 * - current: identifica a tab activa: "geral" | "sessoes" | "inscricoes" | "compras" | "avaliacao" | "diplomas"
 * - cursoLabel: opcional, mostrado no breadcrumb (default = `#${cursoId}`)
 */
export default function CursoTabsNav({ cursoId, current, cursoLabel }) {
  const [counts, setCounts] = useState(null);

  useEffect(() => {
    if (!cursoId) { setCounts(null); return; }
    let cancelled = false;
    adminApi.cursoCounts(cursoId).then((c) => {
      if (!cancelled) setCounts(c);
    }).catch(() => { /* silent: badges são best-effort */ });
    return () => { cancelled = true; };
  }, [cursoId]);

  if (!cursoId) return null;

  const tabs = [
    { id: "geral",      label: "Geral",      icon: BookOpen,        href: `/admin/cursos/edit?curso=${cursoId}`,                       countKey: null },
    { id: "sessoes",    label: "Sessões",    icon: CalendarDays,    href: `/admin/cursos/sessoes?curso=${cursoId}`,                    countKey: "sessoes" },
    { id: "inscricoes", label: "Inscrições", icon: Users,           href: `/admin/cursos/inscricoes?curso=${cursoId}`,                 countKey: "inscricoes" },
    { id: "compras",    label: "Compras",    icon: ShoppingCart,    href: `/admin/cursos/inscricoes?curso=${cursoId}&tab=compras`,     countKey: "compras" },
    { id: "documentos", label: "Documentos", icon: FileDown,        href: `/admin/cursos/inscricoes?curso=${cursoId}&tab=documentos`,  countKey: null },
    { id: "presencas",  label: "Presenças",  icon: CalendarCheck,   href: `/admin/cursos/presencas?curso=${cursoId}`,                  countKey: null },
    { id: "avaliacao",  label: "Avaliação",  icon: ClipboardCheck,  href: `/admin/cursos/inscricoes?curso=${cursoId}&tab=avaliacao`,   countKey: "avaliacoes" },
    { id: "formadores", label: "Formadores", icon: UserCog,         href: `/admin/cursos/formadores?curso=${cursoId}`,                 countKey: null },
    { id: "contratos",  label: "Contratos",  icon: FileSignature,   href: `/admin/cursos/contratos?curso=${cursoId}`,                  countKey: null },
    { id: "certificacao", label: "Certificação", icon: ShieldCheck,  href: `/admin/cursos/certificacao?curso=${cursoId}`,               countKey: null },
    { id: "notas",      label: "Notas",      icon: GraduationCap,   href: `/admin/cursos/notas?curso=${cursoId}`,                      countKey: null },
    { id: "diplomas",   label: "Diplomas",   icon: Award,           href: `/admin/cursos/inscricoes?curso=${cursoId}&tab=diplomas`,    countKey: "diplomas" },
    { id: "dtp",        label: "DTP",        icon: FolderCog,       href: `/admin/cursos/dtp?curso=${cursoId}`,                        countKey: null },
    { id: "cdn",        label: "CDN",        icon: HardDrive,       href: `/admin/cursos/inscricoes?curso=${cursoId}&tab=cdn`,         countKey: null },
  ];

  return (
    <nav
      data-testid="curso-tabs-nav"
      className="mb-4 bg-white border border-[#E6E1D6] rounded-md overflow-hidden"
    >
      <div className="px-4 pt-3 pb-1 flex flex-wrap items-baseline gap-2 border-b border-[#F3F0E7]">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#978D78] font-bold">
          Curso
        </span>
        <span className="text-sm font-semibold text-[#1B1B1B]">
          {cursoLabel || `#${cursoId}`}
          {counts?.id_produto != null && (
            <span
              data-testid="curso-nav-id-produto"
              className="ml-1.5 text-xs font-normal text-[#978D78]"
            >
              (Produto #{counts.id_produto})
            </span>
          )}
        </span>
      </div>
      <div className="px-2 flex flex-wrap gap-1">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = t.id === current;
          const count = t.countKey ? counts?.[t.countKey] : null;
          return (
            <Link
              key={t.id}
              to={t.href}
              data-testid={`curso-nav-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm border-b-2 transition ${
                isActive
                  ? "border-[#A8843C] text-[#A8843C] font-semibold"
                  : "border-transparent text-[#6E6754] hover:text-[#1B1B1B]"
              }`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {count != null && count > 0 && (
                <span
                  data-testid={`curso-nav-tab-${t.id}-count`}
                  className={`ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                    isActive
                      ? "bg-[#A8843C] text-white"
                      : "bg-[#F3F0E7] text-[#6E6754]"
                  }`}
                >
                  {count}
                </span>
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
